import { joinByTime, type ChartPoint } from 'models/chart'
import { saveAs } from 'utils/saveAs'

export type CsvCell = string | number | undefined

export interface CsvColumn {
  header: string
  points: ChartPoint[]
}

/** ISO date (UTC), so the file sorts and imports cleanly in spreadsheets. */
const isoDate = (time: number): string => new Date(time).toISOString().slice(0, 10)

/** RFC 4180: a cell with a comma, quote or line break is quoted, its quotes doubled. */
const toCell = (cell: CsvCell): string => {
  const text = cell === undefined ? '' : String(cell)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** One line per row; an undefined cell is left empty. */
export const toCsv = (rows: CsvCell[][]): string => rows.map((row) => row.map(toCell).join(',')).join('\n')

/** `Date,<column>…` rows, oldest first, as a spreadsheet expects; a missing value is an empty cell. */
const toChartRows = (columns: CsvColumn[]): CsvCell[][] => [
  ['Date', ...columns.map((column) => column.header)],
  ...joinByTime(columns.map((column) => column.points)).map(({ time, values }) => [isoDate(time), ...values]),
]

export const toChartCsv = (columns: CsvColumn[]): string => toCsv(toChartRows(columns))

/** Characters no file system accepts in a name become a dash. */
const safeFileName = (name: string): string => name.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'STOKR'

/** Makes Excel read the file as UTF-8, so labels such as "€" survive. */
const BYTE_ORDER_MARK = '﻿'

/** Saves rows (a header and at least one line) as `<fileName>.csv`; nothing to save, no file. */
export const downloadCsv = (rows: CsvCell[][], fileName: string): void => {
  if (rows.length < 2) return
  saveAs(new Blob([BYTE_ORDER_MARK, toCsv(rows)], { type: 'text/csv;charset=utf-8' }), `${safeFileName(fileName)}.csv`)
}

/** Downloads a chart's data, e.g. `STRC - price history.csv`. */
export const downloadChartCsv = (columns: CsvColumn[], fileName: string): void =>
  downloadCsv(toChartRows(columns), fileName)
